import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, Typography, Box, TextField,
  MenuItem, Select, InputLabel, FormControl, Grid, Button,
  Chip, Stack, InputAdornment
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { createQuoteAndSendMessage } from '../../actions/quote';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import LaptopMacOutlinedIcon from '@mui/icons-material/LaptopMacOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { dinero } from '../../utils/dinero';
import { MINIMO_COBRO } from '../../utils/reglasPago';

const VIGENCIAS = ['24 horas', '48 horas', '3 días', '5 días', '7 días'];
const MODALIDADES_CARDS = [
  { id: 'Presencial', title: 'Presencial', desc: 'Sitio proveedor', icon: <PlaceOutlinedIcon /> },
  { id: 'Remoto', title: 'Remoto', desc: 'Online', icon: <LaptopMacOutlinedIcon /> },
  { id: 'Domicilio', title: 'Domicilio', desc: 'Sitio Cliente', icon: <HomeOutlinedIcon /> },
];

const ModalCotizar = ({ open, onClose, currentChat }) => {
  const dispatch = useDispatch();
  const authUser = useSelector(state => state.auth.user || state.user.user);

  const [concepto, setConcepto] = useState('');
  const [modalidadesSel, setModalidadesSel] = useState([]);
  const [idioma, setIdioma] = useState('Español');
  const [costo, setCosto] = useState('');
  const [unidad, setUnidad] = useState('proyecto');
  const [vigencia, setVigencia] = useState('48 horas');
  const [notas, setNotas] = useState('');
  const [loading, setLoading] = useState(false);

  // Modalidades ofrecibles = las que el proveedor guardó en el servicio.
  // Si el chat no está ligado a un servicio (o no tiene ninguna), se muestran las tres.
  const modsServicio = currentChat?.tema?.modalidades || [];
  const modsFiltradas = MODALIDADES_CARDS.filter(m => modsServicio.includes(m.id));
  const modalidadesDisponibles = modsFiltradas.length ? modsFiltradas : MODALIDADES_CARDS;

  // Al abrir el modal, preseleccionar todas las modalidades disponibles del servicio
  useEffect(() => {
    if (open) {
      setModalidadesSel(modalidadesDisponibles.map(m => m.id));
    }
  }, [open, currentChat]); // eslint-disable-line

  const toggleModalidad = (id) => {
    setModalidadesSel(prev => prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id]);
  };

  const getStrId = (id) => {
    if (!id) return '';
    if (typeof id === 'string') return id;
    if (typeof id === 'object' && id._id) return id._id.toString();
    return String(id);
  };

  // Una cotización también se paga en la plataforma: lo mínimo que se cobra son $10
  const costoValido = parseFloat(costo) >= MINIMO_COBRO;

  const handleEnviar = async () => {
    if (!concepto || !modalidadesSel.length || !costoValido) return;
    setLoading(true);
    try {
      const otherParticipant = currentChat?.participants?.find(p => getStrId(p._id) !== getStrId(authUser?._id));
      if (!otherParticipant) {
        alert('No se pudo encontrar al destinatario de la cotización');
        setLoading(false);
        return;
      }

      await dispatch(createQuoteAndSendMessage(currentChat._id, {
        concepto,
        modalidades: modalidadesSel,
        idioma,
        costo: Number(costo),
        unidad: unidad || 'servicio',
        vigencia,
        notas,
        user: getStrId(otherParticipant._id),
        servicio: currentChat?.relatedType === 'service' ? getStrId(currentChat.relatedId) : null
      }));

      // Reset states
      setConcepto('');
      setModalidadesSel([]);
      setIdioma('Español');
      setCosto('');
      setUnidad('proyecto');
      setVigencia('48 horas');
      setNotas('');
      onClose();
    } catch (err) {
      console.error(err);
      alert('Error al enviar la cotización');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogContent sx={{ p: 4 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
          <DescriptionOutlinedIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 800 }}>
            Datos de la cotización
          </Typography>
        </Stack>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
            Concepto <Box component="span" sx={{ color: 'error.oscuro' }}>*</Box>
          </Typography>
          <TextField
            fullWidth
            placeholder="Ej: Clases privadas de azúcar jalado e isomalt"
            value={concepto}
            onChange={(e) => setConcepto(e.target.value)}
            size="small"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
            Modalidad <Box component="span" sx={{ color: 'error.oscuro' }}>*</Box>
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
            Ofrece una o varias. El cliente elegirá la que prefiera al reservar.
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            {modalidadesDisponibles.map((mod) => {
              const sel = modalidadesSel.includes(mod.id);
              return (
                <Box
                  key={mod.id}
                  onClick={() => toggleModalidad(mod.id)}
                  sx={{
                    flex: 1, minWidth: 140, p: 1.5, border: '1px solid',
                    borderColor: sel ? 'primary.main' : 'divider',
                    borderRadius: '8px', cursor: 'pointer', position: 'relative',
                    bgcolor: sel ? 'grey.50' : 'white',
                    display: 'flex', alignItems: 'center', gap: 1.5,
                    '&:hover': { borderColor: 'primary.main' }
                  }}
                >
                  {sel && (
                    <CheckCircleIcon sx={{ position: 'absolute', top: 6, right: 6, fontSize: 18, color: 'primary.main' }} />
                  )}
                  <Box sx={{
                    width: 36, height: 36, borderRadius: '8px',
                    bgcolor: (t) => alpha(t.palette.secondary.main, 0.1), display: 'flex', alignItems: 'center',
                    justifyContent: 'center', color: 'primary.main'
                  }}>
                    {mod.icon}
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1.2 }}>
                      {mod.title}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {mod.desc}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <LanguageOutlinedIcon fontSize="small" sx={{ color: 'primary.main' }} />
            Idioma del servicio <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>(opcional)</Box>
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={idioma}
              onChange={(e) => setIdioma(e.target.value)}
              sx={{ borderRadius: '8px' }}
            >
              <MenuItem value="Español">Español</MenuItem>
              <MenuItem value="Inglés">Inglés</MenuItem>
              <MenuItem value="Francés">Francés</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={6}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
              Costo (MXN) <Box component="span" sx={{ color: 'error.oscuro' }}>*</Box>
            </Typography>
            <TextField
              fullWidth
              placeholder="0.00"
              value={costo}
              onChange={(e) => setCosto(e.target.value)}
              error={costo !== '' && !costoValido}
              helperText={costo !== '' && !costoValido ? `Lo mínimo es ${dinero(MINIMO_COBRO)}` : ''}
              size="small"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box component="span" sx={{ color: 'primary.main', fontWeight: 'bold' }}>$</Box>
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            />
            {/* Desglose de la comisión: el precio cotizado también paga el 10% */}
            {parseFloat(costo) > 0 && (() => {
              const c = parseFloat(costo);
              const comision = Math.round(c * 10) / 100;
              const neto = Math.round((c - comision) * 100) / 100;
              return (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'success.oscuro' }}>
                  Recibirás <strong>{dinero(neto)}</strong> —
                  comisión de la plataforma 10% ({dinero(comision)}).
                </Typography>
              );
            })()}
          </Grid>
          <Grid size={6}>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
              Unidad
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={unidad}
                onChange={(e) => setUnidad(e.target.value)}
                sx={{ borderRadius: '8px' }}
                displayEmpty
              >
                <MenuItem value="" disabled>Seleccionar unidad</MenuItem>
                <MenuItem value="hora">Por hora</MenuItem>
                <MenuItem value="proyecto">Por proyecto</MenuItem>
                <MenuItem value="sesion">Por sesión</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeOutlinedIcon fontSize="small" sx={{ color: 'primary.main' }} />
            Vigencia de la cotización
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            {VIGENCIAS.map((v) => (
              <Chip
                key={v}
                label={v}
                onClick={() => setVigencia(v)}
                sx={{
                  bgcolor: vigencia === v ? 'primary.main' : 'transparent',
                  color: vigencia === v ? 'white' : 'text.secondary',
                  border: '1px solid',
                  borderColor: vigencia === v ? 'primary.main' : 'divider',
                  '&:hover': { bgcolor: vigencia === v ? 'primary.main' : 'sutil' }
                }}
              />
            ))}
          </Stack>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
            Notas adicionales <Box component="span" sx={{ color: 'text.secondary', fontWeight: 400 }}>(opcional)</Box>
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Incluye detalles extra como materiales incluidos, lugar de reunión, condiciones..."
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
          />
        </Box>

        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="contained"
            color="error"
            onClick={onClose}
            sx={{
              borderRadius: '8px',
              px: 4, py: 1, textTransform: 'none', boxShadow: 'none',
              fontWeight: 700
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleEnviar}
            disabled={!concepto || !modalidadesSel.length || !costoValido || loading}
            sx={{
              bgcolor: 'primary.main', color: 'white', borderRadius: '8px',
              px: 4, py: 1, textTransform: 'none', boxShadow: 'none',
              '&:hover': { bgcolor: 'primary.dark' }, fontWeight: 700
            }}
          >
            {loading ? 'Enviando...' : 'Enviar'}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default ModalCotizar;
